import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Home, Sparkles } from 'lucide-react';
import PageHeader from './PageHeader';
import { API_BASE_URL } from '../config/api';

const TourPage = () => {
  const { tourId, stepNum } = useParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState(null);
  const currentStep = parseInt(stepNum) || 1;

  useEffect(() => {
    const fetchTour = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/tours/tour/${tourId}`);
        
        if (!response.ok) {
          console.error(`Tour ${tourId} not found`);
          navigate('/sample-gallery');
          return;
        }
        
        const tourData = await response.json();
        setTour(tourData);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load tour:', error);
        navigate('/sample-gallery');
      }
    };

    fetchTour();
  }, [tourId, navigate]);

  const handleNext = () => {
    if (currentStep < tour.steps.length) {
      navigate(`/tour/${tourId}/step/${currentStep + 1}`);
      window.scrollTo(0, 0);
    } else {
      // Tour complete - check if last step has a custom CTA link
      const lastStep = tour.steps[tour.steps.length - 1];
      if (lastStep.ctaLink) {
        navigate(lastStep.ctaLink);
      } else if (tourId.startsWith('sample-story-')) {
        navigate('/compiler');
      } else {
        // For demo-onboarding tour, stay on demo page
        navigate('/compiler');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigate(`/tour/${tourId}/step/${currentStep - 1}`);
      window.scrollTo(0, 0);
    } else {
      navigate('/sample-gallery');
    }
  };

  const getThemeColors = () => {
    const themes = {
      'blue': { bg: 'from-blue-500 to-cyan-500', text: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
      'amber': { bg: 'from-amber-500 to-orange-500', text: 'text-amber-600', button: 'bg-amber-600 hover:bg-amber-700' },
      'red': { bg: 'from-red-500 to-rose-500', text: 'text-red-600', button: 'bg-red-600 hover:bg-red-700' },
      'purple': { bg: 'from-purple-500 to-pink-500', text: 'text-purple-600', button: 'bg-purple-600 hover:bg-purple-700' },
      'indigo': { bg: 'from-indigo-500 to-purple-500', text: 'text-indigo-600', button: 'bg-indigo-600 hover:bg-indigo-700' },
      'green': { bg: 'from-green-500 to-emerald-500', text: 'text-green-600', button: 'bg-green-600 hover:bg-green-700' },
    };
    return themes[tour?.theme] || themes['blue'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="h-12 w-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading tour...</p>
        </div>
      </div>
    );
  }

  if (!tour || !tour.steps || tour.steps.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 mb-4">Tour not found</p>
          <Button onClick={() => navigate('/sample-gallery')}>
            Back to Gallery
          </Button>
        </div>
      </div>
    );
  }

  const step = tour.steps[currentStep - 1];
  const theme = getThemeColors();

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader borderBottom={true} />
      
      {/* Tour Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between mb-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/sample-gallery')}
              className="flex items-center gap-2"
            >
              <Home className="h-4 w-4" />
              Back to Gallery
            </Button>
            <div className="text-sm text-slate-600">
              Step {currentStep} of {tour.steps.length}
            </div>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r ${theme.bg} transition-all duration-300`}
              style={{ width: `${(currentStep / tour.steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Step Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 md:p-12 mb-8">
            {/* Icon - if available */}
            {step.icon && (
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${theme.bg} text-white mb-6`}>
                <Sparkles className="h-8 w-8" />
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {step.title}
            </h1>

            {/* Description */}
            {step.description && (
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {step.description}
              </p>
            )}

            {/* Learning Points */}
            {step.learningPoints && step.learningPoints.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-slate-800 mb-3">What You'll Learn:</h3>
                <ul className="space-y-2">
                  {step.learningPoints.map((point, idx) => (
                    <li key={idx} className="text-slate-700">• {point}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Points/Bullets */}
            {step.points && step.points.length > 0 && (
              <div className="space-y-3 mb-6">
                {step.points.map((point, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r ${theme.bg} flex items-center justify-center text-white text-xs font-bold`}>
                      {idx + 1}
                    </div>
                    <p className="text-slate-700 pt-0.5">{point}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Use Case */}
            {step.useCase && (
              <div className={`bg-gradient-to-r ${theme.bg} bg-opacity-10 border-l-4 border-${tour.theme}-500 p-4 rounded-r-lg mb-6`}>
                <p className="font-semibold text-slate-800 mb-1">💡 Use Case:</p>
                <p className="text-slate-700">{step.useCase}</p>
              </div>
            )}

            {/* Tip */}
            {step.tip && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                <p className="text-amber-900">
                  <span className="font-semibold">💡 Pro Tip:</span> {step.tip}
                </p>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              {currentStep === 1 ? 'Back to Gallery' : 'Previous'}
            </Button>

            <Button
              onClick={handleNext}
              className={`flex items-center gap-2 ${theme.button}`}
            >
              {currentStep === tour.steps.length 
                ? (step.ctaText || (tourId.startsWith('sample-story-') 
                    ? 'Go to Compiler →'
                    : 'Start Compiling →'))
                : 'Next Step'}
              {!step.ctaText && <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TourPage;
